import { useCallback, useEffect, useRef, useState } from 'react';
import { OTHER_ACTIONS, actionAnchorId } from '../configs/otherActionsConfig';

/**
 * Ephemeral focus / inject controller for the "Autres actions" launcher.
 *
 * Selecting an action either **focuses** an already-mounted card (smooth-scroll
 * + **expand** + a transient highlight, all applied **imperatively** on the
 * card's anchor element) or **injects** one instance just above the panel and
 * then focuses it. State is plain React `useState` — it resets on reload /
 * navigation; there is no backend, no persistence (per spec).
 *
 * The cards self-anchor (`id={actionAnchorId(componentId)}` on their root), so
 * the page stays clean: it renders existing cards exactly as today and renders
 * the injected ones as bare fragments above the panel, while this hook reaches
 * each card's anchor through the DOM. The highlight is applied/removed on the
 * element's `classList` and the expand is a `click()` on the card's own
 * collapsible header — never a per-card prop. The cards stay pure.
 *
 * The focus itself is the exported standalone {@link focusCardAnchor}, so any
 * other caller (e.g. a table row's pencil) can bring a mounted card into view
 * the same way.
 */

/** How long the highlight ring stays on the focused card (ms). */
export const HIGHLIGHT_DURATION_MS = 1800;

/** Tailwind ring classes toggled on the anchor element during a flash. */
const HIGHLIGHT_CLASSES = ['ring-2', 'ring-primary-400', 'ring-offset-2'];

/** Resolve a launcher key to its backing card's registered component id. */
function componentIdForKey(key: string): string | undefined {
  return OTHER_ACTIONS.find((action) => action.key === key)?.componentId;
}

/**
 * Expand the card rooted at `root`, if it is currently collapsed.
 *
 * A collapsible `Card.Header` renders `role="button"` + `aria-expanded` (see
 * `getCollapsibleProps` in `src/components/base/layout/Card/Card.tsx`), so the
 * card's own header is the **first** `[aria-expanded]` element in the anchor
 * subtree — the header is the article's first child, so anything else that
 * carries `aria-expanded` (a `Select` in the body, a nested collapsible) always
 * comes later in document order and can never be mistaken for it.
 *
 * Clicking that header runs the card's real toggle, so no card needs a new prop
 * for this — the same reach-through-the-DOM style as the highlight above.
 *
 * The click fires **only** when the header reads `aria-expanded="false"`:
 * re-selecting an already-open card must be a no-op, never a toggle that folds
 * it shut. A card the user collapses by hand afterwards keeps its own state —
 * this only governs the moment the launcher focuses it.
 */
function expandCard(root: HTMLElement): void {
  const header = root.querySelector<HTMLElement>('[aria-expanded]');
  if (header === null) return;
  if (header.getAttribute('aria-expanded') !== 'false') return;
  // Guarded like `scrollIntoView` below, so a stubbed/partial element in a test
  // env cannot throw.
  if (typeof header.click === 'function') header.click();
}

/** A pending highlight-clear timer. */
type HighlightTimer = ReturnType<typeof setTimeout>;

/** Options of {@link focusCardAnchor} — the defaults suit a standalone caller. */
export interface FocusCardAnchorOptions {
  /**
   * Expand the card right away (default `true`). The launcher's injected path
   * passes `false` and expands one frame later (see the hook's effect).
   */
  expand?: boolean;
  /**
   * Registry the highlight-clear timer is kept in while pending, so an owner
   * (the launcher hook) can cancel it on unmount. Omitted ⇒ the timer is
   * fire-and-forget — it only removes classes from the anchor element.
   */
  highlightTimers?: Set<HighlightTimer>;
}

/** Ring `el` with the highlight classes for {@link HIGHLIGHT_DURATION_MS}. */
function flashHighlight(el: HTMLElement, highlightTimers?: Set<HighlightTimer>): void {
  el.classList.add(...HIGHLIGHT_CLASSES);
  const timer = setTimeout(() => {
    el.classList.remove(...HIGHLIGHT_CLASSES);
    highlightTimers?.delete(timer);
  }, HIGHLIGHT_DURATION_MS);
  highlightTimers?.add(timer);
}

/**
 * Bring an already-mounted claim card into view: smooth-scroll to its anchor
 * (`id={actionAnchorId(componentId)}` on the card's root), flash the highlight
 * ring and expand it when it is collapsed — the launcher's "focus" path, as a
 * standalone helper any caller can use (e.g. a table row's pencil asking the
 * « Modifier un intervenant » card to edit its intervenant, after
 * `useClaimDataStore.getState().requestEditIntervenant(idSinp)`).
 *
 * Everything goes through the DOM (no per-card prop), so the card stays pure.
 * The expand clicks the card's own collapsible header and only when it reads
 * `aria-expanded="false"` — focusing an open card never folds it.
 *
 * @returns `false` when the anchor is not mounted (nothing happens) — the
 *   caller decides what to do then (the launcher hook injects the card).
 */
export function focusCardAnchor(
  componentId: string,
  options: FocusCardAnchorOptions = {},
): boolean {
  const el = document.getElementById(actionAnchorId(componentId));
  if (el === null) return false;
  // `scrollIntoView` is undefined in jsdom / some test envs — guard it with
  // a runtime typeof check (the DOM types claim it is always present).
  if (typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
  flashHighlight(el, options.highlightTimers);
  if (options.expand !== false) expandCard(el);
  return true;
}

export interface UseActionInjectionResult {
  /** Keys whose card has been injected at the bottom of the column (dedup'd). */
  injectedKeys: string[];
  /**
   * Focus the card for `key` if its anchor exists, else inject it then focus it.
   */
  selectAction: (key: string) => void;
}

export function useActionInjection(): UseActionInjectionResult {
  const [injectedKeys, setInjectedKeys] = useState<string[]>([]);

  // Key awaiting a scroll/highlight after an injection-driven re-render.
  const pendingScrollKey = useRef<string | null>(null);
  // Active highlight-clear timers, cleared on unmount.
  const timers = useRef<Set<HighlightTimer>>(new Set());
  // Pending deferred-expand frames, cancelled on unmount.
  const frames = useRef<Set<number>>(new Set());

  /** Expand the card for `componentId` if its anchor is mounted and collapsed. */
  const expandFocused = useCallback((componentId: string) => {
    const el = document.getElementById(actionAnchorId(componentId));
    if (el !== null) expandCard(el);
  }, []);

  const selectAction = useCallback(
    (key: string) => {
      const componentId = componentIdForKey(key);
      if (componentId === undefined) return;

      // Already mounted → focus it (scroll + highlight + expand), never
      // duplicate. The DOM has long settled here (we are inside the launcher's
      // own click handler), so the header's `aria-expanded` is authoritative
      // and the expand can run right away.
      if (focusCardAnchor(componentId, { highlightTimers: timers.current })) {
        return;
      }
      // Not mounted → inject (dedup), then focus once the DOM updates (effect on
      // `injectedKeys` below).
      pendingScrollKey.current = key;
      setInjectedKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
    },
    [],
  );

  // After an injection mounts the new card, scroll to + highlight + expand it.
  //
  // The expand — and only the expand — must be deferred one frame. `CardRoot`
  // seeds its collapse state from `defaultExpanded` inside its *own*
  // `useLayoutEffect`. That runs during the mount commit, ahead of this passive
  // effect, but the state update it schedules has not committed yet: React
  // flushes pending passive effects *before* the sync re-render a layout
  // effect's `setState` triggers, so this effect still runs against the
  // pre-seed DOM. On this tick the header therefore still shows `CardRoot`'s
  // initial `useState(true)` — i.e. `aria-expanded` reads `"true"` for a card
  // that is about to render collapsed. Reading it here would wrongly conclude
  // "already open" and silently skip the expand.
  // Measured in `useActionInjection.test.tsx` ("reads a stale aria-expanded on
  // the mount tick, so one tick is not enough"). A frame later the seed has
  // committed and the attribute is authoritative — this is a real ordering
  // dependency, not a timing fudge, so it uses `requestAnimationFrame`, never a
  // guessed delay.
  useEffect(() => {
    const key = pendingScrollKey.current;
    if (key === null) return;
    pendingScrollKey.current = null;
    const componentId = componentIdForKey(key);
    if (componentId === undefined) return;
    // Scroll + highlight now; the expand waits for the next frame (below).
    focusCardAnchor(componentId, { expand: false, highlightTimers: timers.current });
    const frame = requestAnimationFrame(() => {
      frames.current.delete(frame);
      expandFocused(componentId);
    });
    frames.current.add(frame);
  }, [injectedKeys, expandFocused]);

  // Clear any pending highlight timers / expand frames on unmount.
  useEffect(() => {
    const pendingTimers = timers.current;
    const pendingFrames = frames.current;
    return () => {
      pendingTimers.forEach((timer) => { clearTimeout(timer); });
      pendingTimers.clear();
      pendingFrames.forEach((frame) => { cancelAnimationFrame(frame); });
      pendingFrames.clear();
    };
  }, []);

  return { injectedKeys, selectAction };
}
